
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import JsonLd from '../../components/seo/JsonLd';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "Service",
        "position": 1,
        "name": "Power of Attorney Notarization",
        "description": "Legal authorization documents allowing someone to act on your behalf for financial, legal, or healthcare decisions.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "GCM Mobile Notary"
        },
        "offers": {
          "@type": "Offer",
          "price": "15",
          "priceCurrency": "USD",
          "description": "Starting at $15 per signature"
        }
      },
      {
        "@type": "Service",
        "position": 2,
        "name": "Real Estate Document Notarization",
        "description": "Property transaction notarization including deeds, mortgages, and real estate contracts.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "GCM Mobile Notary"
        },
        "offers": {
          "@type": "Offer",
          "price": "15",
          "priceCurrency": "USD",
          "description": "Starting at $15 per signature"
        }
      },
      {
        "@type": "Service",
        "position": 3,
        "name": "Apostille Services",
        "description": "Document authentication for international use, making your documents legally recognized abroad.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "GCM Mobile Notary"
        }
      }
    ]
  };

  const mainServices = [
    {
      icon: 'ri-file-shield-line',
      title: 'Power of Attorney',
      description: 'Legal authorization documents allowing someone to act on your behalf for financial, legal, or healthcare decisions.',
      features: ['Financial Power of Attorney', 'Durable Power of Attorney', 'Limited Power of Attorney', 'General Power of Attorney'],
      price: 'Starting at $15 per signature'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Medical Power of Attorney',
      description: 'Healthcare decision authorization documents for medical situations when you cannot make decisions yourself.',
      features: ['Healthcare Directives', 'Medical Decision Authorization', 'HIPAA Authorization Forms', 'Living Will Notarization'],
      price: 'Starting at $15 per signature'
    },
    {
      icon: 'ri-file-text-line',
      title: 'Living Trusts & Wills',
      description: 'Estate planning document notarization to protect your assets and ensure your wishes are legally documented.',
      features: ['Living Trust Documents', 'Last Will & Testament', 'Trust Amendments', 'Estate Planning Forms'],
      price: 'Starting at $15 per signature'
    },
    {
      icon: 'ri-passport-line',
      title: 'Travel Consent Forms',
      description: 'Authorization documents for minors traveling domestically or internationally without both parents.',
      features: ['Minor Travel Consent', 'International Travel Forms', 'Custody Authorization', 'Grandparent Travel Consent'],
      price: 'Starting at $15 per signature'
    },
    {
      icon: 'ri-home-4-line',
      title: 'Real Estate Documents',
      description: 'Property transaction notarization including deeds, mortgages, and real estate contracts.',
      features: ['Deed Notarization', 'Mortgage Documents', 'Property Transfer Forms', 'Real Estate Contracts'],
      price: 'Starting at $15 per signature'
    },
    {
      icon: 'ri-award-line',
      title: 'Apostille Services',
      description: 'Document authentication for international use, making your documents legally recognized abroad.',
      features: ['Document Authentication', 'International Certification', 'Embassy Legalization', 'Foreign Document Prep'],
      price: 'Contact for pricing'
    }
  ];

  const additionalServices = [
    {
      icon: 'ri-truck-line',
      title: 'Courier Services',
      description: 'Secure legal document delivery from point A to point B throughout the San Fernando Valley.'
    },
    {
      icon: 'ri-file-copy-line',
      title: 'Document Copying',
      description: 'Certified copies of important documents for legal and official purposes.'
    },
    {
      icon: 'ri-translate-2',
      title: 'Document Translation',
      description: 'Professional translation services for legal documents requiring notarization in multiple languages.'
    },
    {
      icon: 'ri-user-settings-line',
      title: 'Loan Signing Services',
      description: 'Professional loan document signing for mortgages, refinances, and home equity loans.'
    },
    {
      icon: 'ri-calendar-check-line',
      title: 'Same-Day Service',
      description: 'Emergency and urgent notarization appointments available throughout the valley.'
    }
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Contact Us',
      description: 'Call or text to schedule your mobile notary appointment'
    },
    {
      step: '2',
      title: 'We Come to You',
      description: 'Our notary travels to your preferred location at the scheduled time'
    },
    {
      step: '3',
      title: 'Verify & Notarize',
      description: 'We verify your identity and properly notarize your documents'
    },
    {
      step: '4',
      title: 'Complete & Secure',
      description: 'Your documents are properly executed and ready for use'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Mobile Notary Services Sherman Oaks - Power of Attorney, Real Estate, Apostille"
        description="Comprehensive mobile notary services in Sherman Oaks. Power of Attorney, Real Estate Documents, Apostille Services, Living Trusts. Professional notarization at your location."
        keywords="Mobile Notary Services Sherman Oaks, Power of Attorney Notary, Real Estate Document Notarization, Apostille Services California, Living Trust Notary, Travel Consent Forms"
        canonical={`${siteUrl}/services`}
        ogTitle="Mobile Notary Services Sherman Oaks - Power of Attorney, Real Estate, Apostille"
        ogDescription="Comprehensive mobile notary services in Sherman Oaks. Power of Attorney, Real Estate Documents, Apostille Services, Living Trusts. Professional notarization at your location."
        ogUrl={`${siteUrl}/services`}
        ogImage={`${siteUrl}/og-image.jpg`}
      />
      <JsonLd data={servicesSchema} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Professional Mobile Notary Services
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Comprehensive notarization services delivered to your location throughout Sherman Oaks 
            and the San Fernando Valley. Fast, reliable, and professional.
          </p>
          <a
            href="tel:+18772113011"
            className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            Call (877) 211-3011
          </a>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Notary Services
            </h2>
            <p className="text-xl text-gray-600">
              Professional notarization for all your important legal documents
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {mainServices.map((service, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${service.icon} text-2xl text-orange-500`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Includes:</h4>
                      <ul className="space-y-1">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-600">
                            <i className="ri-check-line text-orange-500 mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="text-orange-500 font-semibold">{service.price}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Living Trust Creation Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <i className="ri-file-shield-line text-3xl text-orange-500"></i>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Create Your Own Living Trust Online
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Need a Living Trust but want to create it yourself? We've partnered with Everything Living Trusts 
                  to offer you a convenient online solution. Create your trust documents from the comfort of your home, 
                  then we'll notarize them for you.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Step-by-step guided process</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Attorney-reviewed templates</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Customized for California law</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <i className="ri-check-circle-line text-orange-500 text-xl"></i>
                    <span className="text-gray-700">Professional notarization included</span>
                  </div>
                </div>

                <div className="bg-orange-50 p-6 rounded-xl mb-8">
                  <h3 className="font-bold text-gray-900 mb-2">Complete Package:</h3>
                  <p className="text-gray-700">
                    Create your Living Trust online + Professional mobile notarization service 
                    at your location throughout the San Fernando Valley.
                  </p>
                </div>

                <a
                  href="https://www.everythinglivingtrusts.com?aff=af58f8d9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  Create Your Living Trust
                </a>
              </div>
              
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/e91f7a94087bd326d6cc96a32dab8fa1.jpg"
                  alt="Create Living Trust Online"
                  className="rounded-xl shadow-lg object-cover w-full h-96"
                />
                <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-500">24/7</div>
                    <div className="text-sm text-gray-600">Online Access</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Additional Services</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-truck-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Courier Services</h4>
              <p className="text-gray-600">Document pickup and delivery throughout the San Fernando Valley</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-file-copy-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Document Copying</h4>
              <p className="text-gray-600">Professional copying and scanning services on-site</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-home-heart-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Loan Signing Agent</h4>
              <p className="text-gray-600">Certified loan signing services for real estate transactions</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Same-Day Service</h4>
              <p className="text-gray-600">Urgent notarization needs handled promptly</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-translate-2 text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Document Translation</h4>
              <p className="text-gray-600">Professional translation services for legal documents requiring notarization</p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-hospital-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Hospital Notarization</h4>
              <p className="text-gray-600">Specialized notary services at hospitals and medical facilities</p>
            </div>
          </div>

          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-government-line text-xl text-orange-600"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Jail Notarization</h4>
              <p className="text-gray-600">Professional notary services at correctional facilities and detention centers</p>
            </div>
          </div>
        </div>
      </div>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How Our Mobile Notary Process Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple, convenient, and professional service in 4 easy steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-orange-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Fair, competitive rates for professional mobile notary services
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Standard Notarization</h3>
              <div className="text-3xl font-bold text-orange-500 mb-4">$15 per signature</div>
              <p className="text-gray-600 mb-6">Plus travel fee</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Single document notarization
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Identity verification
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Official notary seal
                </li>
              </ul>
            </div>

            {/* Mobile Service Card */}
            <div className="bg-white p-8 rounded-xl shadow-lg border-2 border-orange-500">
              <div className="text-center mb-4">
                <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">Most Popular</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Real Estate Documents</h3>
              <div className="text-3xl font-bold text-orange-500 mb-4">Contact</div>
              <p className="text-gray-600 mb-6">For pricing details</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Property transfers & deeds
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Mortgage documents
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Real estate contracts
                </li>
              </ul>
            </div>

            {/* Apostille Service Card */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Apostille Service</h3>
              <div className="text-3xl font-bold text-orange-500 mb-4">Contact</div>
              <p className="text-gray-600 mb-6">For pricing details</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Document authentication
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  International certification
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-orange-500 mr-2"></i>
                  Embassy processing
                </li>
              </ul>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">
              * Travel fees may apply for locations outside the standard service area
            </p>
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Quote Now
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          
          <div className="space-y-8">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What do I need to bring for notarization?</h3>
              <p className="text-gray-600">
                You'll need a valid government-issued photo ID (driver's license, passport, or state ID) 
                and the unsigned documents that require notarization. Do not sign the documents beforehand.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">How quickly can you come to my location?</h3>
              <p className="text-gray-600">
                We offer same-day service and can typically arrive within 2-4 hours of your call. 
                Emergency appointments are available for urgent situations.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">Do you provide services on weekends?</h3>
              <p className="text-gray-600">
                Yes, we offer mobile notary services 7 days a week, including evenings and weekends. 
                Additional fees may apply for after-hours service.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-3">What areas do you serve?</h3>
              <p className="text-gray-600">
                We serve Sherman Oaks and the entire San Fernando Valley, including Encino, Van Nuys, 
                North Hollywood, Studio City, Woodland Hills, and surrounding communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Schedule Your Mobile Notary Service?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Professional, convenient, and reliable notarization services delivered to your location. 
            Contact us today to schedule your appointment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18772113011"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (877) 211-3011
            </a>
            <Link
              to="/contact"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
                  Schedule Online
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
